import gsap from "gsap";
import {
	DETAIL_CARD_FADE_DELAY_S,
	DETAIL_CARD_FADE_DURATION_S,
	DETAIL_ENTRANCE_DIVIDER_STAGGER_S,
	DETAIL_EVENT_SLIDE_CONTENT_DELAY_S,
	DETAIL_EVENT_SLIDE_CONTENT_DURATION_S,
	DETAIL_EVENT_SLIDE_CONTENT_STAGGER_S,
	DETAIL_EVENT_SLIDE_CLOSE_CONTENT_DURATION_S,
	DETAIL_EVENT_SLIDE_CLOSE_CONTENT_STAGGER_S,
	DETAIL_EVENT_SLIDE_CLOSE_HEIGHT_DELAY_S,
	DETAIL_EVENT_SLIDE_CLOSE_HEIGHT_DURATION_S,
	DETAIL_EVENT_SLIDE_CONTENT_Y_PX,
	DETAIL_EVENT_SLIDE_HEIGHT_DURATION_S,
	DETAIL_EVENT_SLIDE_HEIGHT_EASE,
	DETAIL_EXPLORE_EXTRA_DELAY_S,
	GRID_ENTRANCE_HR_DURATION_S,
	GRID_ENTRANCE_HR_EASE,
	HOVER_EXPLORE_DURATION_S,
	HOVER_FACTS_DURATION_S,
	HOVER_FACTS_EXTRA_DELAY_S,
	HOVER_FACTS_STAGGER_S,
	HOVER_FACTS_Y_PX,
} from "./timeline-constants";
import { getHorizontalScaleOrigin, prefersReducedMotion } from "./timeline-utils";

function waitForDetailLayoutFonts(): Promise<void> {
	if (!document.fonts?.ready) {
		return Promise.resolve();
	}

	return document.fonts.ready.then(() => undefined);
}

export function resetDetailCardEntranceStyles(card: HTMLElement): void {
	const cardInner = card.querySelector<HTMLElement>(".historical-timeline__detail-card-inner");
	const exploreElements = Array.from(
		card.querySelectorAll<HTMLElement>(
			".historical-timeline__explore-line, .historical-timeline__explore-years"
		)
	);
	const factRows = Array.from(
		card.querySelectorAll<HTMLElement>("[data-decade-facts-host] [data-fact-row]")
	);

	const targets = [cardInner, ...exploreElements, ...factRows].filter(
		(element): element is HTMLElement => element !== null
	);

	if (targets.length === 0) {
		return;
	}

	gsap.killTweensOf(targets);

	if (cardInner) {
		gsap.set(cardInner, { clearProps: "opacity" });
	}

	if (exploreElements.length > 0) {
		gsap.set(exploreElements, { clearProps: "opacity" });
	}

	if (factRows.length > 0) {
		gsap.set(factRows, { clearProps: "opacity,y" });
	}
}

export function animateHorizontalDividerEntrance(
	divider: HTMLElement,
	delay = 0
): gsap.core.Tween | undefined {
	if (prefersReducedMotion()) {
		gsap.set(divider, { scaleX: 1 });
		return undefined;
	}

	gsap.set(divider, { scaleX: 0, transformOrigin: getHorizontalScaleOrigin() });
	return gsap.to(divider, {
		scaleX: 1,
		duration: GRID_ENTRANCE_HR_DURATION_S,
		delay,
		ease: GRID_ENTRANCE_HR_EASE,
		overwrite: "auto",
	});
}

export function animateDetailCardEntrance(cardInner: HTMLElement): gsap.core.Tween | undefined {
	if (prefersReducedMotion()) {
		gsap.set(cardInner, { opacity: 1 });
		return undefined;
	}

	gsap.set(cardInner, { opacity: 0 });
	return gsap.to(cardInner, {
		opacity: 1,
		duration: DETAIL_CARD_FADE_DURATION_S,
		delay: DETAIL_CARD_FADE_DELAY_S,
		ease: "power2.out",
		overwrite: "auto",
	});
}

export function animateDetailExploreLabel(container: HTMLElement): gsap.core.Tween | undefined {
	const elements = [
		container.querySelector<HTMLElement>(".historical-timeline__explore-line"),
		container.querySelector<HTMLElement>(".historical-timeline__explore-years"),
	].filter((element): element is HTMLElement => element !== null);

	if (elements.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		gsap.set(elements, { opacity: 1 });
		return undefined;
	}

	gsap.set(elements, { opacity: 0 });
	return gsap.to(elements, {
		opacity: 1,
		duration: HOVER_EXPLORE_DURATION_S,
		delay: DETAIL_CARD_FADE_DELAY_S + DETAIL_EXPLORE_EXTRA_DELAY_S,
		ease: "power2.out",
		overwrite: "auto",
	});
}

export function animateDetailCardFacts(cardInner: HTMLElement): gsap.core.Tween | undefined {
	const elements = Array.from(
		cardInner.querySelectorAll<HTMLElement>("[data-decade-facts-host] [data-fact-row]")
	);

	if (elements.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		gsap.set(elements, { opacity: 1, y: 0 });
		return undefined;
	}

	const baseDelay =
		DETAIL_CARD_FADE_DELAY_S + DETAIL_EXPLORE_EXTRA_DELAY_S + HOVER_FACTS_EXTRA_DELAY_S;

	gsap.set(elements, { opacity: 0, y: HOVER_FACTS_Y_PX });
	return gsap.to(elements, {
		opacity: 1,
		y: 0,
		duration: HOVER_FACTS_DURATION_S,
		stagger: HOVER_FACTS_STAGGER_S,
		delay: baseDelay,
		ease: "power2.out",
		overwrite: "auto",
	});
}

export function runDetailEntranceAnimations(
	card: HTMLElement,
	yearsHost: HTMLElement,
	options: { animateCard: boolean } = { animateCard: true }
): gsap.core.Tween[] {
	const tweens: gsap.core.Tween[] = [];

	if (options.animateCard) {
		const cardInner = card.querySelector<HTMLElement>(".historical-timeline__detail-card-inner");
		if (cardInner) {
			const cardTween = animateDetailCardEntrance(cardInner);
			if (cardTween) {
				tweens.push(cardTween);
			}

			const exploreTween = animateDetailExploreLabel(cardInner);
			if (exploreTween) {
				tweens.push(exploreTween);
			}

			const factsTween = animateDetailCardFacts(cardInner);
			if (factsTween) {
				tweens.push(factsTween);
			}
		}
	}

	const dividers = yearsHost.querySelectorAll<HTMLElement>(".historical-timeline__detail-year-divider");
	dividers.forEach((divider, index) => {
		const dividerTween = animateHorizontalDividerEntrance(
			divider,
			index * DETAIL_ENTRANCE_DIVIDER_STAGGER_S
		);
		if (dividerTween) {
			tweens.push(dividerTween);
		}
	});

	return tweens;
}

function getActiveEventSlide(eventHost: HTMLElement, activeSlideIndex = 0): HTMLElement | null {
	const slides = eventHost.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-slide");
	return slides[activeSlideIndex] ?? slides[0] ?? null;
}

function getActiveEventCard(eventHost: HTMLElement, activeSlideIndex?: number): HTMLElement | null {
	const activeFromSwiper = eventHost.querySelector<HTMLElement>(
		".swiper-slide-active .historical-timeline__detail-event-card"
	);
	if (activeFromSwiper) {
		return activeFromSwiper;
	}

	const activeSlide = getActiveEventSlide(eventHost, activeSlideIndex ?? 0);
	return activeSlide?.querySelector<HTMLElement>(".historical-timeline__detail-event-card") ?? null;
}

function getDetailEventHostTargetHeight(eventHost: HTMLElement, activeSlideIndex = 0): number {
	const activeSlide = getActiveEventSlide(eventHost, activeSlideIndex);
	return activeSlide?.offsetHeight ?? eventHost.offsetHeight;
}

function getDetailEventMediaStaggerTargets(card: HTMLElement): HTMLElement[] {
	const mediaSwiperWrap = card.querySelector<HTMLElement>(".historical-timeline__detail-event-media-swiper-wrap");
	if (mediaSwiperWrap) {
		return [mediaSwiperWrap];
	}

	return [
		...Array.from(card.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-image-wrap")),
		...Array.from(card.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-caption")),
	];
}

function getDetailEventStaggerTargets(eventHost: HTMLElement, activeSlideIndex?: number): HTMLElement[] {
	const card = getActiveEventCard(eventHost, activeSlideIndex);
	if (!card) {
		return [];
	}

	return [
		card.querySelector<HTMLElement>(".historical-timeline__detail-event-title"),
		card.querySelector<HTMLElement>(".historical-timeline__detail-event-description"),
		...Array.from(card.querySelectorAll<HTMLElement>("[data-fact-row]")),
		...getDetailEventMediaStaggerTargets(card),
		card.querySelector<HTMLElement>(".historical-timeline__detail-copy-wrap"),
	].filter((element): element is HTMLElement => element !== null);
}

export function resetDetailEventHostStyles(eventHost: HTMLElement | null): void {
	if (!eventHost) {
		return;
	}

	gsap.killTweensOf(eventHost);
	gsap.set(eventHost, { clearProps: "height,overflow" });

	const staggerTargets = getDetailEventStaggerTargets(eventHost);
	if (staggerTargets.length > 0) {
		gsap.killTweensOf(staggerTargets);
		gsap.set(staggerTargets, { clearProps: "opacity,y" });
	}
}

export function animateDetailEventSlideClose(eventHost: HTMLElement): {
	timeline: gsap.core.Timeline;
	complete: Promise<void>;
} {
	const staggerTargets = getDetailEventStaggerTargets(eventHost);
	const row = eventHost.closest<HTMLElement>(".historical-timeline__detail-year");
	const closingDivider = row?.querySelector<HTMLElement>(
		".historical-timeline__detail-year-divider--after-slide"
	);

	if (prefersReducedMotion()) {
		return {
			timeline: gsap.timeline(),
			complete: Promise.resolve(),
		};
	}

	if (staggerTargets.length === 0) {
		return {
			timeline: gsap.timeline(),
			complete: Promise.resolve(),
		};
	}

	const currentHeight = eventHost.offsetHeight;
	gsap.set(eventHost, { height: currentHeight, overflow: "hidden" });

	let resolveComplete: () => void = () => undefined;
	const complete = new Promise<void>((resolve) => {
		resolveComplete = resolve;
	});

	const timeline = gsap.timeline({
		onComplete: resolveComplete,
	});

	timeline.to(staggerTargets, {
		opacity: 0,
		y: -DETAIL_EVENT_SLIDE_CONTENT_Y_PX,
		duration: DETAIL_EVENT_SLIDE_CLOSE_CONTENT_DURATION_S,
		stagger: {
			each: DETAIL_EVENT_SLIDE_CLOSE_CONTENT_STAGGER_S,
			from: "end",
		},
		ease: "power2.in",
		overwrite: "auto",
	});

	timeline.to(
		eventHost,
		{
			height: 0,
			duration: DETAIL_EVENT_SLIDE_CLOSE_HEIGHT_DURATION_S,
			ease: DETAIL_EVENT_SLIDE_HEIGHT_EASE,
			overwrite: "auto",
		},
		`+=${DETAIL_EVENT_SLIDE_CLOSE_HEIGHT_DELAY_S}`
	);

	if (closingDivider) {
		timeline.to(
			closingDivider,
			{
				scaleX: 0,
				opacity: 0,
				duration: DETAIL_EVENT_SLIDE_CLOSE_HEIGHT_DURATION_S,
				ease: DETAIL_EVENT_SLIDE_HEIGHT_EASE,
				transformOrigin: getHorizontalScaleOrigin(),
				overwrite: "auto",
			},
			"<"
		);
	}

	return { timeline, complete };
}

export function animateDetailEventSlide(
	eventHost: HTMLElement,
	options: {
		animateHeight?: boolean;
		activeSlideIndex?: number;
		onHeightComplete?: () => void;
	} = {}
): gsap.core.Tween[] {
	const tweens: gsap.core.Tween[] = [];
	const { animateHeight = true, activeSlideIndex = 0, onHeightComplete } = options;
	const staggerTargets = getDetailEventStaggerTargets(eventHost, activeSlideIndex);

	if (prefersReducedMotion()) {
		gsap.set(eventHost, { height: "auto", overflow: "visible" });
		if (staggerTargets.length > 0) {
			gsap.set(staggerTargets, { opacity: 1, y: 0 });
		}
		onHeightComplete?.();
		return tweens;
	}

	if (staggerTargets.length === 0) {
		gsap.set(eventHost, { height: "auto", overflow: "visible" });
		onHeightComplete?.();
		return tweens;
	}

	gsap.set(staggerTargets, { opacity: 0, y: DETAIL_EVENT_SLIDE_CONTENT_Y_PX });

	if (animateHeight) {
		const measureHeight = () => getDetailEventHostTargetHeight(eventHost, activeSlideIndex);

		gsap.set(eventHost, { height: "auto", overflow: "hidden" });
		const targetHeight = measureHeight();
		gsap.set(eventHost, { height: 0, overflow: "hidden" });

		const heightTween = gsap.to(eventHost, {
			height: targetHeight,
			duration: DETAIL_EVENT_SLIDE_HEIGHT_DURATION_S,
			ease: DETAIL_EVENT_SLIDE_HEIGHT_EASE,
			overwrite: "auto",
			onComplete: () => {
				gsap.set(eventHost, { height: "auto", clearProps: "overflow" });
				onHeightComplete?.();
			},
		});
		tweens.push(heightTween);

		void waitForDetailLayoutFonts().then(() => {
			if (!eventHost.isConnected) {
				return;
			}

			const nextHeight = measureHeight();
			if (nextHeight > targetHeight + 1) {
				gsap.set(eventHost, { height: nextHeight });
			}
		});
	} else {
		gsap.set(eventHost, { height: "auto", overflow: "visible" });
		onHeightComplete?.();
	}

	const contentTween = gsap.to(staggerTargets, {
		opacity: 1,
		y: 0,
		duration: DETAIL_EVENT_SLIDE_CONTENT_DURATION_S,
		stagger: DETAIL_EVENT_SLIDE_CONTENT_STAGGER_S,
		delay: animateHeight ? DETAIL_EVENT_SLIDE_CONTENT_DELAY_S : 0,
		ease: "power2.out",
		overwrite: "auto",
	});
	tweens.push(contentTween);

	return tweens;
}
